import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

import { activeBeat, type BookTimelineLayout, type SceneBeat } from "@/lib/timeline/book/BookLayout";
import { deathMomentAt } from "@/lib/timeline/marker/DeathMoment/moment";
import { MARKER_DIAMETER } from "@/lib/timeline/marker/MarkerMetrics/metrics";
import type { PlaybackState } from "@/lib/timeline/playback";

type Death = NonNullable<SceneBeat["deaths"]>[number];

interface TimelineDeathsProps {
  canvas: HTMLCanvasElement | null;
  layout: BookTimelineLayout;
  getPlayback: () => PlaybackState;
}

const NONE: readonly Death[] = [];

const markerOf = (id: string): HTMLElement | null => document.querySelector<HTMLElement>(
  `[data-marker-part="disc"][data-character-id="${CSS.escape(id)}"]`,
);

const faceOf = (id: string): HTMLElement | null => (
  markerOf(id)?.querySelector<HTMLElement>(".timeline-marker__disc") ?? null
);

function seatOf(marker: HTMLElement): { x: number; y: number } | null {
  const match = /translate\((-?[\d.]+)px, (-?[\d.]+)px\)/.exec(marker.style.transform);
  return match === null ? null : { x: Number(match[1]), y: Number(match[2]) };
}

/** Someone who dies in a scene: their portrait grays out, with a label saying what happened. */
export function TimelineDeaths({ canvas, layout, getPlayback }: TimelineDeathsProps) {
  const [deaths, setDeaths] = useState(NONE);
  const hostRef = useRef<Element | null>(null);
  const grayed = useRef(new Set<string>());

  useEffect(() => {
    if (canvas === null) {
      return;
    }
    const seen = grayed.current;
    const onFrame = (): void => {
      hostRef.current ??= document.querySelector(".timeline-marker-layer--discs");
      const playheadMs = getPlayback().playheadMs;
      const beat = activeBeat(layout, playheadMs);
      const here = beat?.deaths ?? NONE;
      setDeaths(here);
      for (const id of seen) {
        if (!here.some((death) => death.characterId === id)) {
          faceOf(id)?.style.setProperty("filter", "");
          seen.delete(id);
        }
      }
      if (beat === undefined) {
        return;
      }
      const moment = deathMomentAt((playheadMs - beat.startMs) / (beat.endMs - beat.startMs));
      for (const death of here) {
        seen.add(death.characterId);
        faceOf(death.characterId)?.style.setProperty(
          "filter",
          `grayscale(${moment.grayscale}) opacity(${moment.opacity})`,
        );
        const marker = markerOf(death.characterId);
        const seat = marker === null ? null : seatOf(marker);
        const label = document.querySelector<HTMLElement>(
          `[data-death-label="${CSS.escape(death.characterId)}"]`,
        );
        if (label !== null && seat !== null) {
          label.style.opacity = String(moment.labelOpacity);
          label.style.transform = `translate(${seat.x}px, ${seat.y + MARKER_DIAMETER / 2}px) `
            + "translate(-50%, -50%)";
        }
      }
    };
    // After the portraits have been placed for this frame.
    const afterPortraits = (): void => queueMicrotask(onFrame);
    canvas.addEventListener("timeline-frame", afterPortraits);
    return () => {
      canvas.removeEventListener("timeline-frame", afterPortraits);
      for (const id of seen) {
        faceOf(id)?.style.setProperty("filter", "");
      }
      seen.clear();
    };
  }, [canvas, getPlayback, layout]);

  const host = hostRef.current;
  return host === null ? null : createPortal(
    deaths.map((death) => (
      <div
        key={death.characterId}
        data-death-label={death.characterId}
        className="timeline-death-label"
      >
        {death.label}
      </div>
    )),
    host,
  );
}
